import React, { useEffect, useState, useMemo } from "react";
import {
  Spin,
  Typography,
  Card,
  Input,
  Button,
  message,
  Icon,
  Popconfirm,
  Modal,
  Radio,
} from "antd";
import copy from "copy-to-clipboard";
import api from "../../../services/api";
import { useFreire } from "../../../utils/freireContext";
import { formatters } from "../../../utils/formatters";
import CountryFlag from "../../../components/SelectCountry/CountryFlag";
import PaymentHistory from "./PaymentHistory";
import Show from "../../../contexts/PermissionContext/Show";
import * as text from "../locales";

const { Text } = Typography;
const STRIPE_PAYMENT_LINK_TEMPLATE =
  "https://buy.stripe.com/aFa6oG3ZkegkfKMd1Fgw000?prefilled_email=";

const Wallets = ({ user_id }) => {
  const { freire } = useFreire();
  const [wallets, setWallets] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWalletId, setSelectedWalletId] = useState(null);
  const [countries, setCountries] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [balanceInput, setBalanceInput] = useState("");
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [refundModalWallet, setRefundModalWallet] = useState(null);
  const [refundType, setRefundType] = useState("full");
  const [refundValue, setRefundValue] = useState("");
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundPopConfirmVisible, setRefundPopConfirmVisible] = useState(false);

  const sanitizedRefundValue = !refundValue
    ? NaN
    : Number(refundValue.replace(/,/g, "."));
  const isPartialRefundInvalid =
    refundType === "partial" &&
    (!refundValue ||
      Number.isNaN(sanitizedRefundValue) ||
      sanitizedRefundValue <= 0 ||
      sanitizedRefundValue > (refundModalWallet?.balance || 0) / 100);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${user_id}/wallets`);
      setWallets(data.wallets || []);
      setUser(data.user || null);
      // Fetch country data for flags/currency
      const countriesResp = await api.get("/countries/recommended");
      setCountries(countriesResp.data.countries || []);
      // Default wallet: match user country/currency, else first
      let defaultWallet = data.wallets.find(
        (w) =>
          w.country === data.user.country && w.currency === data.user.currency
      );
      if (!defaultWallet) defaultWallet = data.wallets[0];
      setSelectedWalletId(defaultWallet?._id);
      setBalanceInput(""); // reset input on wallet/user change
    } catch (e) {
      // handle error
    } finally {
      setLoading(false);
    }
  }, [user_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selectedWallet = useMemo(
    () => wallets.find((w) => w._id === selectedWalletId),
    [wallets, selectedWalletId]
  );

  useEffect(() => {
    if (selectedWalletId && selectedWallet) {
      setBalanceInput((selectedWallet.balance / 100).toFixed(2));
    }
  }, [selectedWalletId, selectedWallet]);

  const getCountryData = (code) => countries.find((c) => c.code === code);

  if (loading) {
    return <Spin style={{ margin: 32 }} />;
  }
  if (!wallets.length) {
    return <div>{freire(text.NO_WALLETS_FOUND)}</div>;
  }

  const handleBalanceUpdate = async () => {
    setUpdateLoading(true);
    setUpdateError(null);
    try {
      const newBalanceCents = Math.round(
        parseFloat(balanceInput.replace(/,/g, ".")) * 100
      );
      await api.put(`/admin/users/${user_id}/wallets/${selectedWallet._id}`, {
        balance: newBalanceCents,
      });
      // Refresh wallets
      const { data } = await api.get(`/admin/users/${user_id}/wallets`);
      setWallets(data.wallets || []);
      message.success(freire(text.BALANCE_UPDATE_SUCCESS));
    } catch (e) {
      setUpdateError(freire(text.BALANCE_UPDATE_ERROR));
      message.error(freire(text.BALANCE_UPDATE_ERROR));
    } finally {
      setUpdateLoading(false);
    }
  };

  const resetRefundState = () => {
    setRefundType("full");
    setRefundValue("");
    setRefundPopConfirmVisible(false);
  };

  const openWalletRefundModal = (wallet) => {
    setRefundModalWallet(wallet);
    setRefundModalVisible(true);
    resetRefundState();
  };

  const closeWalletRefundModal = () => {
    setRefundModalVisible(false);
    setRefundModalWallet(null);
    resetRefundState();
  };

  const handleWalletRefund = async () => {
    if (!refundModalWallet) return;
    const walletId = refundModalWallet._id;

    let refundAmountCents = refundModalWallet.balance;
    if (refundType === "partial") {
      const parsedValue = parseFloat(refundValue.replace(/,/g, "."));
      if (Number.isNaN(parsedValue)) {
        message.error(freire(text.ERROR_REQUEST_REFUND));
        return;
      }
      refundAmountCents = Math.round(parsedValue * 100);
    }

    setRefundLoading(true);
    try {
      const { data } = await api.post(
        `/admin/users/${user_id}/wallets/${walletId}/refund`,
        {
          amount: refundAmountCents,
          reason: "manual-admin-refund",
          notifyUser: false,
        }
      );
      const refund = data?.refund;
      const isPartialRefund =
        typeof refund?.refunded === "number" &&
        typeof refund?.total === "number" &&
        refund.refunded < refund.total;
      if (isPartialRefund) {
        const currency =
          refundModalWallet?.currency || selectedWallet?.currency || "BRL";
        const fmt = formatters.currency(user?.language || "pt-BR", currency);
        if (refundType === "full") {
          message.warning(
            `${freire(text.PARTIAL_REFUND_FALLBACK)}: ${fmt(refund.refunded / 100)}`
          );
        } else {
          message.success(
            `${freire(text.PARTIAL_REFUND_SUCCESS)}: ${fmt(refund.refunded / 100)}`
          );
        }
      } else {
        message.success(freire(text.REFUND));
      }
      closeWalletRefundModal();
      const previousSelectedWalletId = walletId;
      await fetchData();
      setSelectedWalletId(previousSelectedWalletId);
    } catch (error) {
      const responseMessage = error?.response?.data?.message;
      message.error({
        content: (
          <div>
            <div>{freire(text.ERROR_REQUEST_REFUND)}</div>
            {responseMessage ? (
              <div style={{ marginTop: 4 }}>{responseMessage}</div>
            ) : null}
          </div>
        ),
      });
    } finally {
      setRefundLoading(false);
      setRefundPopConfirmVisible(false);
    }
  };

  const handleCopyStripePaymentLink = () => {
    if (!user?.email) {
      message.error(freire(text.STRIPE_PAYMENT_LINK_EMAIL_MISSING));
      return;
    }
    const linkToCopy = `${STRIPE_PAYMENT_LINK_TEMPLATE}${encodeURIComponent(
      user.email
    )}`;
    const copied = copy(linkToCopy);
    if (copied) {
      message.success(freire(text.STRIPE_PAYMENT_LINK_COPIED));
    } else {
      message.error(freire(text.STRIPE_PAYMENT_LINK_COPY_ERROR));
    }
  };

  return (
    <div>
      <Modal
        title={freire(text.REFUND)}
        visible={refundModalVisible}
        onCancel={closeWalletRefundModal}
        footer={null}
      >
        <div style={{ marginBottom: 16 }}>
          <Radio.Group
            value={refundType}
            onChange={(e) => setRefundType(e.target.value)}
            style={{ marginBottom: 12 }}
          >
            <Radio value="full">{freire(text.FULL_REFUND)}</Radio>
            <Radio value="partial">{freire(text.PARTIAL_REFUND)}</Radio>
          </Radio.Group>
        </div>
        {refundType === "partial" && (
          <Input
            style={{ width: 180, marginBottom: 12 }}
            value={refundValue}
            onChange={(e) => setRefundValue(e.target.value)}
            addonAfter={
              refundModalWallet?.currency || selectedWallet?.currency || "BRL"
            }
            placeholder={freire(text.REFUND_VALUE)}
            type="number"
            min={0}
            max={(refundModalWallet?.balance || 0) / 100}
          />
        )}
        <div style={{ marginBottom: 12, color: "#faad14" }}>
          <Icon type="info-circle" /> {freire(text.REFUND_DB_WEBHOOK_WARNING)}
        </div>
        <Popconfirm
          visible={refundPopConfirmVisible}
          onVisibleChange={setRefundPopConfirmVisible}
          title={
            <div>
              <div>
                {refundType === "full" ? (
                  freire(text.REFUND_FULL_CONFIRM)
                ) : (
                  <>
                    {freire(text.REFUND_CONFIRM)}
                    <br />
                    <strong>{freire(text.OLD_VALUE)}:</strong>{" "}
                    {formatters.currency(
                      user?.language || "pt-BR",
                      refundModalWallet?.currency ||
                        selectedWallet?.currency ||
                        "BRL"
                    )((refundModalWallet?.balance || 0) / 100)}
                    <br />
                    <strong>{freire(text.REFUND_VALUE)}:</strong>{" "}
                    {formatters.currency(
                      user?.language || "pt-BR",
                      refundModalWallet?.currency ||
                        selectedWallet?.currency ||
                        "BRL"
                    )(
                      Number.isNaN(sanitizedRefundValue)
                        ? 0
                        : sanitizedRefundValue
                    )}
                    <br />
                    <strong>{freire(text.WILL_REMAIN)}:</strong>{" "}
                    {formatters.currency(
                      user?.language || "pt-BR",
                      refundModalWallet?.currency ||
                        selectedWallet?.currency ||
                        "BRL"
                    )(
                      (refundModalWallet?.balance || 0) / 100 -
                        (Number.isNaN(sanitizedRefundValue)
                          ? 0
                          : sanitizedRefundValue)
                    )}
                  </>
                )}
              </div>
            </div>
          }
          onConfirm={handleWalletRefund}
          okText={freire(text.YES)}
          cancelText={freire(text.NO)}
          disabled={isPartialRefundInvalid || refundLoading}
        >
          <Button
            type="primary"
            loading={refundLoading}
            disabled={isPartialRefundInvalid || refundLoading}
            onClick={() => setRefundPopConfirmVisible(true)}
            style={{ width: 120 }}
          >
            <Icon type="save" /> {freire(text.REFUND)}
          </Button>
        </Popconfirm>
      </Modal>
      <div className="wallet-card-container">
        {wallets
          .sort((b, a) => a.currency.localeCompare(b.currency))
          .map((wallet) => {
            const country = getCountryData(wallet.country);
            const isSelected = wallet._id === selectedWalletId;
            const isBrazilianWallet =
              (wallet.country || "").toUpperCase() === "BR" ||
              wallet.currency === "BRL";
            return (
              <Card
                key={wallet._id}
                onClick={() => setSelectedWalletId(wallet._id)}
                className={`wallet-card${
                  isSelected ? " wallet-card--selected" : ""
                }`}
                style={{ position: "relative" }}
              >
                {isSelected && (
                  <Button
                    shape="circle"
                    icon="reload"
                    size="small"
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 2,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchData();
                    }}
                  />
                )}
                <div className="wallet-card__flag-row">
                  {country && (
                    <CountryFlag
                      country={country}
                      showFlag
                      style={{ marginRight: 8 }}
                    />
                  )}
                  <span style={{ marginLeft: 16 }}>
                    <Text type="secondary">{wallet.currency}</Text>
                  </span>
                </div>
                <div className="wallet-card__balance">
                  {formatters.currency(
                    user?.language || "pt-BR",
                    wallet.currency
                  )(wallet.balance / 100)}
                </div>
                <Show when="change-wallet-balance">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 12,
                    }}
                  >
                    <Input
                      style={{ width: 200, marginRight: 8 }}
                      defaultValue={wallet.balance / 100}
                      onChange={(e) => setBalanceInput(e.target.value)}
                      disabled={!isSelected}
                      addonAfter={wallet.currency}
                    />
                    <Popconfirm
                      title={
                        <div>
                          <div>{freire(text.UPDATE_BALANCE_CONFIRM)}</div>
                          <div style={{ marginTop: 8 }}>
                            <div
                              className="wallet-card__flag-row"
                              style={{ marginBottom: 0 }}
                            >
                              {country && (
                                <CountryFlag
                                  country={country}
                                  showFlag
                                  style={{ marginRight: 8 }}
                                />
                              )}
                              <span style={{ marginLeft: 16 }}>
                                <Text type="secondary">{wallet.currency}</Text>
                              </span>
                            </div>
                            <strong>{freire(text.BALANCE)}:</strong>{" "}
                            {formatters.currency(
                              user?.language || "pt-BR",
                              wallet.currency
                            )(wallet.balance / 100)}{" "}
                            <Icon type="arrow-right" />{" "}
                            {formatters.currency(
                              user?.language || "pt-BR",
                              wallet.currency
                            )(parseFloat(balanceInput.replace(/,/g, ".")))}
                          </div>
                        </div>
                      }
                      onConfirm={handleBalanceUpdate}
                      okText={freire(text.YES)}
                      cancelText={freire(text.NO)}
                      disabled={
                        !isSelected ||
                        updateLoading ||
                        !balanceInput ||
                        isNaN(Number(balanceInput.replace(/,/g, ".")))
                      }
                    >
                      <Button
                        type="primary"
                        loading={updateLoading}
                        disabled={
                          !isSelected ||
                          updateLoading ||
                          !balanceInput ||
                          isNaN(Number(balanceInput.replace(/,/g, ".")))
                        }
                      >
                        <Icon type="save" />
                      </Button>
                    </Popconfirm>
                    {updateError && (
                      <Text type="danger" style={{ marginLeft: 8 }}>
                        {updateError}
                      </Text>
                    )}
                  </div>
                </Show>
                <Show when="refund-wallet-balance">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 12,
                    }}
                  >
                    <Button
                      icon="undo"
                      disabled={!isSelected}
                      onClick={(e) => {
                        e.stopPropagation();
                        openWalletRefundModal(wallet);
                      }}
                    >
                      {freire(text.REFUND)}
                    </Button>
                  </div>
                </Show>
                {isBrazilianWallet && (
                  <div style={{ marginTop: 12 }}>
                    <Button
                      icon="copy"
                      disabled={!user?.email}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyStripePaymentLink();
                      }}
                    >
                      {freire(text.COPY_STRIPE_PAYMENT_LINK)}
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
      </div>
      {selectedWallet && (
        <PaymentHistory user_id={user_id} wallet_id={selectedWallet._id} />
      )}
    </div>
  );
};

export default Wallets;
